<template>
  <div class="input-material">
    <el-select
      v-bind="$attrs"
      :model-value="modelValue"
      filterable
      remote
      remote-show-suffix
      clearable
      :remote-method="manejarBusqueda"
      :loading="cargando"
      popper-class="material-select-popper"
      value-key="Codigo"
      class="input-material__select"
      @update:model-value="(valor) => emit('update:modelValue', valor)"
      @change="(valor) => emit('change', valor)"
      @visible-change="manejarCambioVisibilidad"
    >
      <template #label="{ value }">
        <material-option-content :material="value" />
      </template>
      <el-option
        v-for="opt in opciones"
        :key="opt.Codigo"
        :label="
          [opt.DescripcionInterna, opt.CodigoAdicional, opt.CodigoEquivalente]
            .filter(Boolean)
            .join(' ')
        "
        :value="opt"
      >
        <material-option-content
          :material="opt"
          :busqueda="textoBusqueda"
        />
      </el-option>
    </el-select>
    <slot />
    <el-tooltip
      v-if="mostrarAbrir && puedeVerMaterial"
      content="Abrir el material en otra pestaña"
      placement="top"
    >
      <el-button
        :disabled="!modelValue?.Codigo"
        size="small"
        text
        :icon="IconOpenInNew"
        @click="abrirMaterialEnNuevaPestania(modelValue)"
      />
    </el-tooltip>

    <!--
      Scroll infinito: un InfiniteLoading teletransportado (Teleport, no como hijo directo del
      <el-select> — eso rompe el escaneo interno de opciones de Element Plus) hacia el scrollbar
      del popper que esté abierto en este momento (ver `manejarCambioVisibilidad`). El
      `IntersectionObserver` que usa la librería necesita que su propio nodo sea descendiente real
      del contenedor con scroll, por eso el Teleport en vez de sólo pasarle `target`.
    -->
    <Teleport
      v-if="popperTarget"
      :to="popperTarget"
    >
      <InfiniteLoading
        :key="`${popperTargetVersion}-${versionLista}`"
        :target="popperTarget"
        :distance="80"
        @infinite="manejarInfinito"
      >
        <template #complete><span /></template>
        <template #error><span /></template>
      </InfiniteLoading>
    </Teleport>
  </div>
</template>

<script setup>
// Selector de material con búsqueda remota y paginación por scroll. `v-model` es el objeto
// material completo (`value-key="Codigo"`). El resto de los atributos (placeholder, size,
// disabled, style...) se reenvían al <el-select>.
//
// No pide nada al montarse: la primera búsqueda se hace recién al abrir el desplegable (o al
// escribir), para que una grilla con muchas filas no dispare una request por fila. Si el
// llamador ya tiene la primera página sin búsqueda, puede pasarla en `precarga` y la apertura no
// vuelve a pedirla.
import {
  computed,
  onBeforeUnmount,
  nextTick,
  ref,
  watch,
} from 'vue';
import { useRouter } from 'vue-router';
import InfiniteLoading from 'v3-infinite-loading';
import 'v3-infinite-loading/lib/style.css';
import IconOpenInNew from '~icons/mdi/open-in-new';
import MaterialService from '@/services/material_service';
import MaterialOptionContent from '@/shared/components/material_option_content.vue';
import { useAuthStore } from '@/views/auth/auth_store';
import { canViewModule } from '@/shared/permissions/permission.utils.js';

defineOptions({ inheritAttrs: false });

/** moduleKey de la ruta de materiales (router/index.js). */
const MODULO_MATERIALES = 'materiales';
const LIMITE_PAGINA_MATERIALES = 50;

const props = defineProps({
  modelValue: {
    type: Object,
    default: null,
  },
  /** Código de la categoría de materiales a buscar. */
  categoria: {
    type: [Number, String],
    default: null,
  },
  codigoFamilia: {
    type: [Number, String],
    default: null,
  },
  /** Bitfield del módulo actual (`currentModule`). */
  modulo: {
    type: [Number, String],
    default: null,
  },
  /**
   * Si es false no se consulta el endpoint y las opciones salen sólo de `filtrarOpciones`
   * (p. ej. categorías de tinta/barniz, o un proceso sin material).
   */
  habilitado: {
    type: Boolean,
    default: true,
  },
  /**
   * Hook del llamador para dar forma a las opciones: recibe la lista cargada y devuelve la lista
   * a mostrar (p. ej. materiales ya elegidos primero y filtro por ancho mínimo).
   */
  filtrarOpciones: {
    type: Function,
    default: null,
  },
  /** Primera página sin búsqueda, si el llamador ya la tiene cargada. */
  precarga: {
    type: Array,
    default: () => [],
  },
  /** Muestra el botón para abrir el material en otra pestaña (si el usuario tiene permiso). */
  mostrarAbrir: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['update:modelValue', 'change', 'materiales-cargados']);

const router = useRouter();
const authStore = useAuthStore();

/**
 * El acceso al ABM de materiales usa el mismo criterio que el guard del router
 * (`canViewModule` sobre el moduleKey 'materiales'). Si se comprobara algo distinto, el botón
 * podría abrir una pestaña que termina en "Acceso restringido".
 */
const puedeVerMaterial = computed(() => canViewModule(authStore.usuario(), MODULO_MATERIALES));

/**
 * Abre el ABM del material en otra pestaña, sin sacar al usuario del documento que está
 * cargando.
 *
 * La ruta se resuelve por nombre para que respete el base del router. `useRouter` devuelve
 * undefined cuando el componente se monta fuera del árbol de rutas —Storybook, por ejemplo,
 * no monta router—, así que ahí se cae a la ruta literal en vez de romper.
 */
const abrirMaterialEnNuevaPestania = (material) => {
  const codigo = material?.Codigo;
  if (!codigo) return;

  const href = router
    ? router.resolve({ name: 'materialEdit', params: { id: codigo } }).href
    : `/page/material/edit/${codigo}`;

  window.open(href, '_blank', 'noopener');
};

const cargando = ref(false);
const cargados = ref([]);
// Mientras no se haga una búsqueda propia, la lista base es `precarga`.
const usaListaPropia = ref(false);
const paginaActual = ref(1);
const hayMasPaginas = ref(true);
let consultaActual = '';
// Texto de la búsqueda vigente, reactivo: lo usa `material-option-content` para resaltar la
// coincidencia en las opciones del desplegable.
const textoBusqueda = ref('');
// Se incrementa cada vez que la lista base se reemplaza (búsqueda nueva, llegada de `precarga`,
// cambio de filtros) y forma parte de la `:key` de `InfiniteLoading`. Una vez que la librería
// recibe `$state.complete()` queda terminada para siempre; sin remontarla, una lista nueva que sí
// tiene más páginas (p. ej. la primera página que llega después de abrir el desplegable vacío, o
// una búsqueda más amplia que la anterior) no volvería a paginar hasta cerrar y reabrir.
const versionLista = ref(0);

const listaBase = computed(() => {
  if (!props.habilitado) return [];
  return usaListaPropia.value ? cargados.value : props.precarga;
});

const opciones = computed(() =>
  props.filtrarOpciones ? props.filtrarOpciones(listaBase.value) : listaBase.value,
);

// El backend pagina de verdad, así que el select necesita búsqueda remota: `buscar` trae como
// máximo 50 por página/término. `append: true` (scroll infinito del popper) suma resultados a la
// lista en vez de reemplazarla y mantiene la búsqueda vigente; una búsqueda nueva siempre
// arranca de la página 1.
async function buscar(query = '', { append = false } = {}) {
  if (!props.habilitado || props.categoria == null) {
    cargados.value = [];
    usaListaPropia.value = true;
    hayMasPaginas.value = false;
    return;
  }
  if (!append) {
    paginaActual.value = 1;
    consultaActual = query;
    textoBusqueda.value = query;
    hayMasPaginas.value = true;
  }
  const filters = {
    categoria: props.categoria,
    page: paginaActual.value,
    limit: LIMITE_PAGINA_MATERIALES,
    modulo: props.modulo,
  };
  if (props.codigoFamilia) filters['codigoFamilia'] = props.codigoFamilia;
  if (query) filters['search'] = query;
  const searchParams = new URLSearchParams(filters);
  // `:loading` en el <el-select> reemplaza toda la lista de opciones por un estado de carga —
  // sirve para una búsqueda nueva (se van a reemplazar igual), pero en un append (scroll
  // infinito) tapa los 50 ya cargados y produce el parpadeo. Ahí el feedback de "cargando" lo da
  // el spinner propio de InfiniteLoading, no el select.
  if (!append) cargando.value = true;
  try {
    const response = await MaterialService.findAllWithQuery(searchParams.toString());
    const nuevos = response?.data ?? [];
    cargados.value = append ? [...listaBase.value, ...nuevos] : nuevos;
    usaListaPropia.value = true;
    hayMasPaginas.value = nuevos.length === LIMITE_PAGINA_MATERIALES;
    // Un append conserva la instancia: es la que espera `loaded()`/`complete()`.
    if (!append) {
      versionLista.value += 1;
      // Una búsqueda nueva reemplaza la lista: el scroll del desplegable vuelve arriba, si no
      // queda en la posición de la lista anterior y los primeros resultados no se ven.
      nextTick(() => {
        if (popperTarget.value) popperTarget.value.scrollTop = 0;
      });
    }
    emit('materiales-cargados', cargados.value);
  } finally {
    if (!append) cargando.value = false;
  }
}

// Handler de `@infinite` de `InfiniteLoading`: pide la página siguiente de la búsqueda vigente
// y la agrega al final de las opciones ya cargadas.
async function manejarInfinito($state) {
  const precargaIncompleta =
    !usaListaPropia.value && props.precarga.length < LIMITE_PAGINA_MATERIALES;
  if (!hayMasPaginas.value || precargaIncompleta) {
    $state?.complete?.();
    return;
  }
  paginaActual.value += 1;
  await buscar(consultaActual, { append: true });
  if (hayMasPaginas.value) {
    $state?.loaded?.();
  } else {
    $state?.complete?.();
  }
}

let temporizadorBusqueda = null;
function manejarBusqueda(query) {
  // Element Plus llama al remote-method con el query vacío cada vez que se REABRE un select
  // remote+filterable (no sólo cuando el usuario escribe) — si ya hay opciones cargadas para
  // "sin búsqueda", no hace falta pedirlas de nuevo. Sin este corte, cada apertura dispara un
  // fetch debounced (300ms) durante el cual la lista queda en un estado transitorio y el
  // dropdown se cierra y reabre solo al llegar la respuesta.
  if (!query && consultaActual === '' && listaBase.value.length > 0) {
    return;
  }
  if (temporizadorBusqueda) clearTimeout(temporizadorBusqueda);
  temporizadorBusqueda = setTimeout(() => buscar(query), 300);
}

// `el-select` mantiene su popper (`persistent: true`, default de Element Plus) SIEMPRE en el DOM
// una vez montado, oculto por CSS cuando está cerrado — no se recrea al abrir. Con varias filas
// en pantalla puede haber más de un `.material-select-popper` coexistiendo (todos menos el
// abierto, ocultos). Por eso no alcanza con buscar el primero: hay que filtrar por cuál está
// realmente visible (`offsetParent`) en el momento en que ESTE select se abre.
// `popperTargetVersion` fuerza a `InfiniteLoading` (ver el <Teleport> del template) a remontarse
// cuando cambia el popper de destino — la librería sólo resuelve su `target` una vez, en su
// propio `onMounted`.
const popperTarget = ref(null);
const popperTargetVersion = ref(0);
function manejarCambioVisibilidad(visible) {
  if (!visible) {
    // Al cerrar se retira el InfiniteLoading, para no dejar uno vivo por cada fila abierta.
    popperTarget.value = null;
    return;
  }
  nextTick(() => {
    let resuelto = null;
    for (const popper of document.querySelectorAll('.material-select-popper')) {
      if (popper.offsetParent !== null) {
        resuelto = popper.querySelector('.el-scrollbar__wrap');
        break;
      }
    }
    popperTarget.value = resuelto;
    popperTargetVersion.value += 1;
  });
}

// Si cambia el filtro de fondo (categoría, familia, módulo) lo cargado deja de valer.
watch(
  () => [props.categoria, props.codigoFamilia, props.modulo],
  () => {
    cargados.value = [];
    usaListaPropia.value = false;
    consultaActual = '';
    textoBusqueda.value = '';
    paginaActual.value = 1;
    hayMasPaginas.value = true;
    versionLista.value += 1;
  },
);

// La `precarga` del padre puede llegar después de abierto el desplegable.
watch(
  () => props.precarga,
  () => {
    if (!usaListaPropia.value) versionLista.value += 1;
  },
);

onBeforeUnmount(() => {
  if (temporizadorBusqueda) clearTimeout(temporizadorBusqueda);
});
</script>

<style scoped>
.input-material {
  display: flex;
  align-items: center;
  gap: 4px;
  /* Cuando la celda es flex (ver `.col-material .cell` en `input_materiales.vue`), el selector
     tiene que seguir ocupando todo el ancho sobrante en vez de encogerse al de su contenido. */
  flex: 1;
  min-width: 0;
}
.input-material__select {
  flex: 1;
  min-width: 0;
  width: 100%;
}
</style>
